import { lazy } from 'react';
import type { RouteObject } from 'react-router-dom';
import { Navigate } from 'react-router-dom';
import ProtectedRoute from '../components/auth/ProtectedRoute';
import StandardStoreGuard from '../components/auth/StandardStoreGuard';
const ShopExplorePage = lazy(() => import('../pages/shop-explore/page'));
const FactoryUploadPage = lazy(() => import('../pages/factory-upload/page'));

const HomePage = lazy(() => import('../pages/home/page'));
const ShopPage = lazy(() => import('../pages/shop/page'));
const SalonStudioPage = lazy(() => import('../pages/salon-studio/page'));
const DeepCustomizationPage = lazy(() => import('../pages/deep-customization/page'));
const HairstyleDesignPage = lazy(() => import('../pages/hairstyle-design/page'));
const MeasurementPage = lazy(() => import('../pages/measurement/page'));
const MeasurementScanPage = lazy(() => import('../pages/measurement-scan/page'));
const LoginPage = lazy(() => import('../pages/login/page'));
const ResetPasswordPage = lazy(() => import('../pages/reset-password/page'));
const CreateAccountPage = lazy(() => import('../pages/create-account/page'));
const VerifyEmailPage = lazy(() => import('../pages/verify-email/page'));
const TryOnUploadPage = lazy(() => import('../pages/try-on-upload/page'));
const ArViewPage = lazy(() => import('../pages/ar-view/page'));
const AiPhotoLibraryPage = lazy(() => import('../pages/ai-photo-library/page'));
const SettingsPage = lazy(() => import('../pages/settings/page'));
const CheckoutPage = lazy(() => import('../pages/checkout/page'));
const OrderSummaryPage = lazy(() => import('../pages/order-summary/page'));
const OrderHistoryPage = lazy(() => import('../pages/order-history/page'));
const AdminOrdersPage = lazy(() => import('../pages/admin-orders/page'));
const NailCustomPage = lazy(() => import('../pages/nail-custom/page'));
const AboutPage = lazy(() => import('../pages/about/page'));
const OurStoryPage = lazy(() => import('../pages/our-story/page'));
const FabricsPage = lazy(() => import('../pages/fabrics/page'));
const CustomizationPage = lazy(() => import('../pages/customization/page'));
const ContactPage = lazy(() => import('../pages/contact/page'));
const ProductDetailPage = lazy(() => import('../pages/product-detail/page'));
const BusinessPartnershipPage = lazy(() => import('../pages/business-partnership/page'));
const CustomOrderReturnPolicyPage = lazy(() => import('../pages/custom-order-return-policy/page'));
const PrivacyPage = lazy(() => import('../pages/privacy/page'));
const CookiePolicyPage = lazy(() => import('../pages/cookie-policy/page'));
const TermsPage = lazy(() => import('../pages/terms/page'));
const NotFoundPage = lazy(() => import('../pages/NotFound'));

const routes: RouteObject[] = [
  { path: '/', element: <HomePage /> },
  {
    path: '/shop',
    element: (
      <StandardStoreGuard>
        <ShopPage />
      </StandardStoreGuard>
    ),
  },
  {
    path: '/shop/explore',
    element: (
      <StandardStoreGuard defaultExclusive>
        <ShopExplorePage />
      </StandardStoreGuard>
    ),
  },
  { path: '/salon-studio', element: <SalonStudioPage /> },
  { path: '/deep-customization', element: <DeepCustomizationPage /> },
  { path: '/clothing-custom', element: <DeepCustomizationPage /> },
  { path: '/hairstyle-design', element: <HairstyleDesignPage /> },
  { path: '/measurement', element: <MeasurementPage /> },
  {
    path: '/measurement/scan',
    element: (
      <ProtectedRoute>
        <MeasurementScanPage />
      </ProtectedRoute>
    ),
  },
  { path: '/login', element: <LoginPage /> },
  { path: '/reset-password', element: <ResetPasswordPage /> },
  { path: '/create-account', element: <CreateAccountPage /> },
  { path: '/verify-email', element: <VerifyEmailPage /> },
  { path: '/generate-avatar', element: <Navigate to="/settings" replace /> },
  { path: '/style-preferences', element: <Navigate to="/try-on/upload" replace /> },
  { path: '/try-on/upload', element: <TryOnUploadPage /> },
  {
    path: '/ar/:linkToken',
    element: (
      <ProtectedRoute>
        <ArViewPage />
      </ProtectedRoute>
    ),
  },
  {
    path: '/my-ai-photos',
    element: (
      <ProtectedRoute>
        <AiPhotoLibraryPage />
      </ProtectedRoute>
    ),
  },
  {
    path: '/settings',
    element: (
      <ProtectedRoute>
        <SettingsPage />
      </ProtectedRoute>
    ),
  },
  {
    path: '/shopping-cart',
    element: <OrderSummaryPage />,
  },
  {
    path: '/order-summary',
    element: <Navigate to="/shopping-cart" replace />,
  },
  {
    path: '/checkout',
    element: <CheckoutPage />,
  },
  {
    path: '/orders',
    element: (
      <ProtectedRoute>
        <OrderHistoryPage />
      </ProtectedRoute>
    ),
  },
  {
    path: '/admin/orders',
    element: (
      <ProtectedRoute requireAdmin>
        <AdminOrdersPage />
      </ProtectedRoute>
    ),
  },
  { path: '/nail-custom', element: <NailCustomPage /> },
  { path: '/about', element: <AboutPage /> },
  { path: '/our-story', element: <OurStoryPage /> },
  { path: '/fabrics', element: <FabricsPage /> },
  { path: '/customization', element: <CustomizationPage /> },
  { path: '/contact', element: <ContactPage /> },
  { path: '/business-partnership', element: <BusinessPartnershipPage /> },
  { path: '/factory-upload', element: <FactoryUploadPage /> },
  { path: '/custom-order-return-policy', element: <CustomOrderReturnPolicyPage /> },
  { path: '/privacy', element: <PrivacyPage /> },
  { path: '/cookie-policy', element: <CookiePolicyPage /> },
  { path: '/terms', element: <TermsPage /> },
  { path: '/product/:id', element: <ProductDetailPage /> },
  { path: '*', element: <NotFoundPage /> },
];

export default routes;
